'use client';

import { useRef, useState, type Ref, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import CustomLink from 'components/shared/CustomLink';
import { homepageCtaProps } from 'components/landing/v3/homepageCta';
import v3 from 'components/landing/v3/v3.module.css';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import type { ProductSpotlightConfig } from './types';
import styles from './MobileStickyCtaBar.module.css';

const MOBILE_BAR_QUERY = '(max-width: 991px)';

type BarLayerProps = {
  product: ProductSpotlightConfig;
  overlay?: boolean;
  active: boolean;
  filled: boolean;
  overlayRef?: Ref<HTMLDivElement>;
};

const BarLayer = ({
  product,
  overlay,
  active,
  filled,
  overlayRef,
}: BarLayerProps) => {
  const isDark = product.theme === 'dark';
  const fill = isDark
    ? filled
      ? styles.layerDarkFilled
      : styles.layerDarkUnfilled
    : `${styles.layerLight} ${
        filled ? styles.layerLightFilled : styles.layerLightUnfilled
      }`;

  return (
    <div
      ref={overlayRef}
      className={`${styles.layer} ${fill} ${overlay ? styles.overlay : ''}`}
      aria-hidden={!active}
      inert={!active}
      style={{ pointerEvents: active ? 'auto' : 'none' }}
    >
      <p
        className={`${styles.productName} ${
          isDark ? styles.productNameDark : styles.productNameLight
        }`}
        aria-hidden="true"
      >
        {product.name}
      </p>
      <div className={styles.ctas}>
        {product.primaryCta ? (
          <CustomLink
            href={product.primaryCta.href}
            className={`${v3.pill} ${v3.pillPrimary} ${styles.barCta}`}
            {...homepageCtaProps(
              product.primaryCta.buttonType,
              product.primaryCta.legacyButtonType,
            )}
            tabIndex={active ? 0 : -1}
          >
            {product.primaryCta.label}
          </CustomLink>
        ) : null}
        {product.unavailableNote ? (
          <p
            className={`${styles.note} ${
              isDark ? styles.noteDark : styles.noteLight
            }`}
          >
            {product.unavailableNote}
          </p>
        ) : null}
        <CustomLink
          href={product.secondaryCta.href}
          className={`${v3.pill} ${v3.pillSecondary} ${
            isDark ? v3.pillSecondaryOnDark : ''
          } ${styles.barCta}`}
          {...homepageCtaProps(
            product.secondaryCta.buttonType,
            product.secondaryCta.legacyButtonType,
          )}
          tabIndex={active ? 0 : -1}
        >
          {product.secondaryCta.label}
        </CustomLink>
      </div>
    </div>
  );
};

type MobileStickyCtaBarProps = {
  pro: ProductSpotlightConfig;
  air?: ProductSpotlightConfig;
  airBlockRef?: RefObject<HTMLElement | null>;
  sectionRef?: RefObject<HTMLDivElement | null>;
};

export const MobileStickyCtaBar = ({
  pro,
  air,
  airBlockRef,
  sectionRef,
}: MobileStickyCtaBarProps) => {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const gsapCtx = useGsapContext(shellRef);
  const [airActive, setAirActive] = useState(false);
  const [pinned, setPinned] = useState(true);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const mm = gsap.matchMedia();

      mm.add(MOBILE_BAR_QUERY, () => {
        const bar = shellRef.current;
        if (!bar) return;

        const overlay = overlayRef.current;
        const airBlock = airBlockRef?.current;
        const section = sectionRef?.current ?? bar;

        const apply = () => {
          const barRect = bar.getBoundingClientRect();
          const nextPinned = barRect.bottom >= window.innerHeight - 2;
          setPinned((prev) => (prev === nextPinned ? prev : nextPinned));

          if (!overlay || !airBlock) return;

          if (!nextPinned) {
            overlay.style.clipPath = 'inset(0% 0 0 0)';
            overlay.style.setProperty('-webkit-clip-path', 'inset(0% 0 0 0)');
            setAirActive((prev) => (prev ? prev : true));
            return;
          }

          const airRect = airBlock.getBoundingClientRect();
          const height = barRect.height || 1;
          const progress = Math.min(
            1,
            Math.max(0, (barRect.bottom - airRect.top) / height),
          );
          const clip = `inset(${((1 - progress) * 100).toFixed(2)}% 0 0 0)`;
          overlay.style.clipPath = clip;
          overlay.style.setProperty('-webkit-clip-path', clip);

          const nextAirActive = progress > 0.5;
          setAirActive((prev) =>
            prev === nextAirActive ? prev : nextAirActive,
          );
        };

        const triggers: ScrollTrigger[] = [
          ScrollTrigger.create({
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            onUpdate: apply,
            onRefresh: apply,
          }),
        ];

        if (overlay && airBlock) {
          triggers.push(
            ScrollTrigger.create({
              trigger: airBlock,
              start: 'top bottom',
              end: 'top top',
              onUpdate: apply,
              onRefresh: apply,
            }),
          );
        }

        apply();

        return () => {
          triggers.forEach((st) => st.kill());
        };
      });

      return () => mm.revert();
    });

    return () => {
      gsapCtx.revert();
    };
  }, [gsapCtx, air, airBlockRef, sectionRef]);

  return (
    <div
      ref={shellRef}
      className={styles.shell}
      role="region"
      aria-label={airActive && air ? air.name : pro.name}
    >
      <BarLayer product={pro} active={!air || !airActive} filled={pinned} />
      {air ? (
        <BarLayer
          product={air}
          overlay
          active={airActive}
          filled={pinned}
          overlayRef={overlayRef}
        />
      ) : null}
    </div>
  );
};
